import { useLayoutEffect } from "react";
import { composerPinSlackPx } from "../-utils/composerLayoutHeight";

const USER_SCROLL_INTENTS = ["pointerdown", "wheel", "keydown"] as const;

/**
 * Holds the list on the latest message until the member reaches for it.
 *
 * The scroller follows size changes one animation frame late. The first page,
 * a host sheet growing to full height, and an older page loaded straight away
 * each painted at the previous offset first, showing the oldest messages
 * before the jump. A resize observer runs after layout and before paint.
 */
export function usePinToLatestUntilUserScrolls(viewport: HTMLElement | null) {
    useLayoutEffect(
        function pinToLatestBeforePaint() {
            if (!viewport || typeof ResizeObserver === "undefined") {
                return;
            }

            let lastScrollTop = viewport.scrollTop;

            // Writing scrollTop directly keeps scroller state updates, and the
            // renders they cause, out of the observer callback.
            const observer = new ResizeObserver(() => {
                viewport.scrollTop = viewport.scrollHeight - viewport.clientHeight;
            });

            // Covers scrolling that sends no pointer or key event first, such
            // as VoiceOver. A taller viewport clamps scrollTop but stays at the end.
            const releaseWhenScrolledUp = () => {
                const scrollTop = viewport.scrollTop;
                const scrolledUp = scrollTop < lastScrollTop - 0.5;
                lastScrollTop = scrollTop;
                const distanceToBottom =
                    viewport.scrollHeight - scrollTop - viewport.clientHeight;

                if (scrolledUp && distanceToBottom > composerPinSlackPx) {
                    release();
                }
            };

            const release = () => {
                observer.disconnect();
                for (const type of USER_SCROLL_INTENTS) {
                    viewport.removeEventListener(type, release);
                }
                viewport.removeEventListener("scroll", releaseWhenScrolledUp);
            };

            observer.observe(viewport);
            if (viewport.firstElementChild) {
                observer.observe(viewport.firstElementChild);
            }
            for (const type of USER_SCROLL_INTENTS) {
                viewport.addEventListener(type, release, { passive: true });
            }
            viewport.addEventListener("scroll", releaseWhenScrolledUp, {
                passive: true,
            });

            return release;
        },
        [viewport],
    );
}
